import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Check, X, Loader2 } from 'lucide-react';
import { useAuth } from '../App';
import CreateAccountPrompt from '../components/CreateAccountPrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const isSuccessPath = window.location.pathname.includes('success');
  const sessionId = searchParams.get('session_id');
  const provider = searchParams.get('provider') || (searchParams.get('token') ? 'paypal' : null);
  // PayPal returns ?token=ORDER_ID&PayerID=... on approve
  const paypalOrderId = searchParams.get('token');

  const [status, setStatus] = useState(isSuccessPath ? 'pending' : 'cancelled');
  const [errorMsg, setErrorMsg] = useState('');
  const [buyer, setBuyer] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!isSuccessPath) return;

    const finalize = async () => {
      try {
        if (provider === 'paypal' && paypalOrderId) {
          // Capture PayPal order
          const res = await axios.post(`${API_URL}/api/payments/paypal/capture/${paypalOrderId}`);
          if (res.data.payment_status === 'paid') {
            setStatus('success');
          } else {
            setStatus('failed');
            setErrorMsg(`PayPal status: ${res.data.status}`);
          }
        } else if (sessionId) {
          // Stripe: poll status up to 8 times (proxy may need a moment to index)
          let attempts = 0;
          let resolved = false;
          while (attempts < 8 && !resolved) {
            const res = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
            if (res.data.payment_status === 'paid') {
              setBuyer({ email: res.data.customer_email, name: res.data.customer_name });
              setStatus('success');
              resolved = true;
              break;
            }
            if (res.data.payment_status === 'unpaid' || res.data.status === 'expired' || res.data.status === 'failed') {
              // Definitive non-paid state from Stripe
              if (attempts >= 4) {
                setStatus('failed');
                setErrorMsg(`Status: ${res.data.status} / ${res.data.payment_status}`);
                resolved = true;
                break;
              }
            }
            attempts += 1;
            await new Promise((r) => setTimeout(r, 2000));
          }
          if (!resolved) {
            // Treat as pending - user can refresh later
            setStatus('failed');
            setErrorMsg('Payment status pending. Please refresh in a moment.');
          }
        } else {
          // No identifiers - assume success on /payment/success path
          setStatus('success');
        }
      } catch (e) {
        setStatus('failed');
        setErrorMsg(e.response?.data?.detail || 'Could not verify payment');
      }
    };

    finalize();
  }, [isSuccessPath, provider, paypalOrderId, sessionId]);

  return (
    <Layout>
      <div className="bg-[#FAFAFA] min-h-screen flex items-center justify-center" data-testid="payment-result-page">
        <div className="max-w-md mx-auto bg-white p-8 border border-[#E2E8F0] rounded-lg shadow-sm text-center">
          {status === 'pending' && (
            <>
              <Loader2 size={36} className="text-[#1D4ED8] animate-spin mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-[#0A0F1C] mb-2">Verifying payment...</h1>
              <p className="text-[#475569] mb-6">Please wait while we confirm your transaction.</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={28} className="text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#0A0F1C] mb-2" data-testid="payment-success">Payment Successful!</h1>
              <p className="text-[#475569] mb-6">Thank you for your purchase. You will receive a confirmation email shortly.</p>
              {user ? (
                <Link to="/portal" className="btn-primary-blue inline-block mb-4" data-testid="payment-result-portal-link">Open my member portal</Link>
              ) : buyer?.email ? (
                <div className="mb-6"><CreateAccountPrompt name={buyer.name} email={buyer.email} /></div>
              ) : null}
            </>
          )}
          {status === 'failed' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X size={28} className="text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#0A0F1C] mb-2" data-testid="payment-failed">Payment Failed</h1>
              <p className="text-[#475569] mb-2">We could not confirm your payment.</p>
              {errorMsg && <p className="text-xs text-[#94A3B8] mb-6">{errorMsg}</p>}
            </>
          )}
          {status === 'cancelled' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X size={28} className="text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#0A0F1C] mb-2" data-testid="payment-cancelled">Payment Cancelled</h1>
              <p className="text-[#475569] mb-6">Your payment was not completed. No charges were made.</p>
            </>
          )}
          <Link to="/" className="btn-primary-blue inline-block" data-testid="payment-result-home-link">Back to Home</Link>
          {status === 'success' && searchParams.get('next') && (
            <div className="mt-3">
              <Link to={searchParams.get('next')} className="text-[#1D4ED8] font-semibold text-sm hover:underline" data-testid="payment-result-next-link">Return to your Clarity Hub →</Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
